import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import OneTimePaymentBanner from '../../components/common/OneTimePaymentBanner';
import { useAuth } from '../../contexts/AuthContext';
import CognitoConfigAlert from '../../components/common/CognitoConfigAlert';
import { configValidation } from '../../utils/cognitoConfig';
import { FiAlertCircle, FiLock } from 'react-icons/fi';
import { userService } from '../../services/userService';
import { paymentService } from '../../services/paymentService';
import ErrorHandler from '../../components/ErrorHandler';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import StripeCardForm from '../../components/payment/StripeCardForm';
import '../../components/payment/Payment.css';
import DOMPurify from 'dompurify'; // Moved import to the top level
import { logger } from '../../utils/logger';
import { scrollToTop } from '../../utils/scrollHelper';

const PageContainer = styled.div`
  padding: 2rem 0;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--dark);
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: #fafafa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-left: 4px solid var(--primary);
`;

const SectionTitle = styled.h3`
  margin-bottom: 1.5rem;
  color: var(--primary);
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 24px;
    background-color: var(--primary);
    margin-right: 12px;
    border-radius: 4px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.columns || '1fr 1fr'};
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease, background-color 0.3s ease;
  
  &:focus {
    outline: none;
  }
  
  ${props => props.error && `
    border-color: #D13046;
    background-color: rgba(45, 49, 66, 0.02);
  `}
`;

const PlanGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const PlanCard = styled.div`
  border: 2px solid ${props => props.selected ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 16px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background-color: white;
  
  &:hover {
    border-color: ${props => props.selected ? 'var(--primary)' : 'var(--primary-light)'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  ${props => props.popular && `
    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background-color: var(--primary);
      color: white;
      padding: 0.25rem 1rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 600;
    }
  `}
`;

const PlanHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PlanName = styled.h4`
  margin: 0;
  color: var(--dark);
`;

const PlanPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
  
  li {
    padding: 0.5rem 0;
    display: flex;
    align-items: center;
    
    &::before {
      content: '✓';
      color: var(--secondary);
      font-weight: bold;
      margin-right: 0.75rem;
    }
  }
`;

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 1px solid var(--primary);
  margin-top: 1rem;
`;

const RadioInput = styled.input`
  margin-right: 0.75rem;
  transform: scale(1.3);
  accent-color: var(--primary);
  cursor: pointer;
`;

const RadioLabel = styled.label`
  font-weight: 600;
  color: var(--primary);
  cursor: pointer;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const CheckboxInput = styled.input`
  margin-top: 0.25rem;
  margin-right: 0.75rem;
`;

const CheckboxLabel = styled.label`
  font-size: 0.95rem;
  line-height: 1.5;
  
  a {
    color: var(--primary);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #2D3142;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: rgba(45, 49, 66, 0.05);
  border-left: 3px solid #2D3142;
  animation: fadeIn 0.3s ease-in-out;
  display: flex;
  align-items: center;
  font-weight: bold;
  
  svg {
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-5px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const LoginLink = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: var(--primary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const DisclaimerText = styled.div`
  background-color: #f5f5f5;
  border-left: 4px solid var(--gray);
  padding: 1rem;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const LoadingContainer = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  transition: background-color 0.3s ease, transform 0.2s ease;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const OrderSummary = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 1.5rem;
  border: 1px solid #e0e0e0;
`;

const OrderSummaryTitle = styled.h4`
  margin-bottom: 1rem;
  color: var(--dark);
  font-weight: 600;
`;

const OrderSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: ${props => props.total ? 'none' : '1px solid #f0f0f0'};
  font-weight: ${props => props.total ? '600' : '400'};
  font-size: ${props => props.total ? '1.1rem' : '1rem'};
  margin-top: ${props => props.total ? '1rem' : '0'};
  
  ${props => props.total && `
    border-top: 1px solid #e0e0e0;
    padding-top: 1rem;
  `}
`;

const SignupPaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signUp, signIn } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  
  // Create refs for error elements
  const errorRef = useRef(null);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    plan: 'basic', // default to basic plan
    agreeTerms: false
  });
  
  // State to track payment form status
  const [paymentStatus, setPaymentStatus] = useState({
    processing: false,
    error: null,
    elementComplete: {
      cardNumber: false,
      cardExpiry: false,
      cardCvc: false
    },
    complete: false
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  
  // Scroll to error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      // Find the first error and scroll to it
      if (errorRef.current) {
        errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [errors]);
  
  // Debug useEffect to monitor payment status changes
  // useEffect(() => {
  //   logger.debug('Payment status updated:', paymentStatus);
  // }, [paymentStatus]);
  
  // Check if a plan was selected from the pricing page
  useEffect(() => {
    // Get the selected plan from URL query parameters
    const queryParams = new URLSearchParams(location.search);
    const planFromQuery = queryParams.get('plan');
    
    if (planFromQuery && (planFromQuery === 'basic' || planFromQuery === 'family')) {
      setFormData(prev => ({
        ...prev,
        plan: planFromQuery
      }));
    }
  }, [location]);
  
  // Plans data
  const plans = {
    basic: {
      name: 'Basic Plan',
      price: '$49',
      priceValue: 49,
      features: [
        'AI-generated personalized sleep plan for one child',
        'Based on your child\'s specific needs',
        'Detailed daily schedule',
        'Bedtime routine recommendations',
        'Night wakings guidance'
      ]
    },
    family: {
      name: 'Family Plan',
      price: '$79',
      priceValue: 79,
      features: [
        'AI-generated personalized sleep plans for up to 2 children',
        'Based on each child\'s specific needs',
        'Detailed daily schedules',
        'Bedtime routine recommendations',
        'Night wakings guidance'
      ],
      popular: true
    }
  };
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
      
      // If clearing email error, also clear general error if it's about existing account
      if (name === 'email' && errors.general && errors.general.includes('already exists')) {
        setErrors(prev => ({
          ...prev,
          general: ''
        }));
      }
    }
  };
  
  const handlePlanSelect = (plan) => {
    setFormData(prev => ({
      ...prev,
      plan
    }));
  };
  
  const handleCardChange = (status) => {
    // Only update if the status has actually changed
    // Use DOMPurify to sanitize any user input if needed
    
    if (status.complete !== paymentStatus.complete || 
        status.error !== paymentStatus.error) {
      
      setPaymentStatus(prev => ({
        ...prev,
        ...status
      }));
  
  // Clear payment error if any
  if (errors.payment) {
    setErrors(prev => ({
      ...prev,
      payment: ''
    }));
  }
  
  // Debug log to help troubleshoot
  logger.debug('Card status updated:', DOMPurify.sanitize(JSON.stringify(status)));
}
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    // Validate account information
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email';
    } else if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Emails do not match';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*[0-9])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one special character';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Validate payment information
    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the Terms and Conditions';
    }
    
    if (paymentStatus.error) {
      newErrors.payment = paymentStatus.error;
    }
    
    // Only check payment completion if there are no other errors
    if (Object.keys(newErrors).length === 0 && !paymentStatus.complete) {
      newErrors.payment = 'Please complete all payment information';
    }
    
    setErrors(newErrors);
    
    // Scroll to the first error if there are any errors
    if (Object.keys(newErrors).length > 0) {
      const firstErrorKey = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(firstErrorKey);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!stripe || !elements) {
      setErrors({ payment: 'Stripe has not been properly initialized' });
      return;
    }
    
    setIsLoading(true);
    
    try {
      // 1. Create payment method with Stripe
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement('cardNumber'),
        billing_details: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email
        }
      });
      
      if (error) {
        setPaymentStatus({ 
          ...paymentStatus,
          error: error.message 
        });
        setIsLoading(false);
        return;
      }
      
      // 2. Call backend to process payment
      const paymentResult = await paymentService.processPayment({
        paymentMethodId: paymentMethod.id,
        amount: formData.plan === 'basic' ? 49 : 79,
        email: formData.email,
        plan: formData.plan,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      if (!paymentResult.success) {
        throw new Error(paymentResult.message || 'Payment failed');
      }
      
      logger.debug('Payment processed successfully:', paymentResult.paymentId);
      
      // 3. Create user account
      const attributes = {
        'given_name': formData.firstName,
        'family_name': formData.lastName,
        'email': formData.email,
        'custom:plan': formData.plan,
        'custom:payment_id': paymentResult.paymentId
      };
      
      const result = await signUp(formData.email, formData.password, attributes);
      
      // 4. Try to sign in automatically
      try {
        const signInResult = await signIn(formData.email, formData.password);
        
        // Save user data
        await userService.saveUserSignup({
          firstName: formData.firstName,
          lastName: formData.lastName,
          selectedPlan: formData.plan,
          email: formData.email,
          paymentMethodId: paymentMethod.id,
          paymentId: paymentResult.paymentId
        }, signInResult.idToken);
        
        // Redirect to dashboard
        navigate('/dashboard');
      } catch (signInError) {
        // Redirect to confirmation page
        navigate('/confirm-signup', { 
          state: { email: formData.email } 
        });
      }
    } catch (error) {
      // Handle specific Cognito error messages
      if (error.code === 'UsernameExistsException') {
        setErrors({
          general: 'An account with this email already exists.'
        });
      } else if (error.code === 'InvalidPasswordException') {
        setErrors({
          general: 'Password does not meet requirements. It must contain at least 8 characters, including uppercase, lowercase, numbers, and special characters.'
        });
      } else {
        setErrors({
          general: error.message || 'Failed to process payment or create account.'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  // Get the selected plan details
  const selectedPlan = plans[formData.plan];
  
  return (
    <div className="container">
      <PageContainer>
        <div className="container mb-4">
          <div className="bg-white p-4 rounded-4 shadow-sm" 
               style={{
                 borderLeft: '4px solid var(--primary)',
                 background: 'linear-gradient(to right, rgba(233, 64, 87, 0.05), rgba(255, 255, 255, 1))',
                 padding: '2rem'
               }}>
            <h2 className="mb-3 text-center" style={{ color: 'var(--primary)', fontWeight: '600', fontSize: '1.5rem' }}>Your Journey to Better Sleep Starts Here</h2>
            <p className="mb-2" style={{ lineHeight: '1.4', fontSize: '0.9rem', maxWidth: '900px', margin: '0 auto' }}>
              Sign up today to receive a customized sleep plan tailored specifically to your baby's unique needs. 
              Our AI-powered system analyzes your baby's specific information and creates personalized recommendations 
              based on trusted guidelines from <span style={{ fontWeight: '500' }}>Triple P, Red Nose Australia, Raising Children Network, and Tresillian</span>. 
              Experience the power of Artificial Intelligence combined with evidence-based sleep practices.
            </p>
          </div>
        </div>
        
        <FormContainer>
          <FormTitle style={{ fontSize: '1.5rem' }}>Create Your Account & Get Your Sleep Plan</FormTitle>
          
          {/* Show Cognito configuration alert if there are issues */}
          {!configValidation.isValid && <CognitoConfigAlert />}
          
          {errors.general && (
            <ErrorHandler error={errors.general} clearError={() => setErrors({...errors, general: ''})} ref={errorRef} />
          )}
          
          <form onSubmit={handleSubmit} noValidate>
            {/* Account Information Section */}
            <FormSection>
              <SectionTitle>Account Information</SectionTitle>
              
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="firstName">First Name</FormLabel>
                  <FormInput
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    error={errors.firstName}
                  />
                  {errors.firstName && <ErrorMessage><FiAlertCircle />{errors.firstName}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="lastName">Last Name</FormLabel>
                  <FormInput
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    error={errors.lastName}
                  />
                  {errors.lastName && <ErrorMessage><FiAlertCircle />{errors.lastName}</ErrorMessage>}
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="email">Email Address</FormLabel>
                  <FormInput
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                  />
                  {errors.email && <ErrorMessage><FiAlertCircle />{errors.email}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="confirmEmail">Confirm Email</FormLabel>
                  <FormInput
                    type="email"
                    id="confirmEmail"
                    name="confirmEmail"
                    value={formData.confirmEmail}
                    onChange={handleChange}
                    error={errors.confirmEmail}
                  />
                  {errors.confirmEmail && <ErrorMessage><FiAlertCircle />{errors.confirmEmail}</ErrorMessage>}
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <FormInput
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={errors.password}
                  />
                  {errors.password && <ErrorMessage><FiAlertCircle />{errors.password}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                  <FormInput
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    error={errors.confirmPassword}
                  />
                  {errors.confirmPassword && <ErrorMessage><FiAlertCircle />{errors.confirmPassword}</ErrorMessage>}
                </FormGroup>
              </FormRow>
            </FormSection>
            
            {/* Plan Selection Section */}
            <FormSection>
              <SectionTitle>Select Your Plan</SectionTitle>
              
              <PlanGrid>
                <PlanCard 
                  selected={formData.plan === 'basic'}
                  onClick={() => handlePlanSelect('basic')}
                >
                  <PlanHeader>
                    <PlanName>{plans.basic.name}</PlanName>
                    <PlanPrice>{plans.basic.price} <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>(one-time payment)</span></PlanPrice>
                  </PlanHeader>
                  
                  <PlanFeatures>
                    {plans.basic.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </PlanFeatures>
                  
                  <RadioContainer>
                    <RadioInput
                      type="radio"
                      id="basicPlan"
                      name="plan"
                      value="basic"
                      checked={formData.plan === 'basic'}
                      onChange={handleChange}
                    />
                    <RadioLabel htmlFor="basicPlan">Select Basic Plan</RadioLabel>
                  </RadioContainer>
                </PlanCard>
                
                <PlanCard 
                  selected={formData.plan === 'family'}
                  onClick={() => handlePlanSelect('family')}
                  popular
                >
                  <PlanHeader>
                    <PlanName>{plans.family.name}</PlanName>
                    <PlanPrice>{plans.family.price} <span style={{ fontSize: '0.9rem', fontWeight: 'normal' }}>(one-time payment)</span></PlanPrice>
                  </PlanHeader>
                  
                  <PlanFeatures>
                    {plans.family.features.map((feature, index) => (
                      <li key={index}>{feature}</li>
                    ))}
                  </PlanFeatures>
                  
                  <RadioContainer>
                    <RadioInput
                      type="radio"
                      id="familyPlan"
                      name="plan"
                      value="family"
                      checked={formData.plan === 'family'}
                      onChange={handleChange}
                    />
                    <RadioLabel htmlFor="familyPlan">Select Family Plan</RadioLabel>
                  </RadioContainer>
                </PlanCard>
              </PlanGrid>
            </FormSection>
            
            {/* Payment Section */}
            <FormSection>
              <SectionTitle>Payment Details</SectionTitle>
              
              <StripeCardForm 
                onChange={handleCardChange}
                errors={errors.payment ? { payment: errors.payment } : null}
              />
              
              {errors.payment && <ErrorMessage><FiAlertCircle />{errors.payment}</ErrorMessage>}
              
              <OrderSummary>
                <OrderSummaryTitle>Order Summary</OrderSummaryTitle>
                <OrderSummaryRow>
                  <span>{selectedPlan.name}</span>
                  <span>{selectedPlan.price}</span>
                </OrderSummaryRow>
                <OrderSummaryRow>
                  <span>GST (included)</span>
                  <span>${(selectedPlan.priceValue * 0.1).toFixed(2)}</span>
                </OrderSummaryRow>
                <OrderSummaryRow total>
                  <span>Total</span>
                  <span>{selectedPlan.price}</span>
                </OrderSummaryRow>
              </OrderSummary>
              
              <br></br>
              <CheckboxContainer>
                <CheckboxInput
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                />
                <CheckboxLabel htmlFor="agreeTerms">
                  I have read and agree to the <Link to="/terms-and-conditions" target="_blank">Terms and Conditions</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>. I understand that my data will be encrypted and stored securely.
                </CheckboxLabel>
              </CheckboxContainer>
              {errors.agreeTerms && <ErrorMessage><FiAlertCircle />{errors.agreeTerms}</ErrorMessage>}
              
              <SubmitButton 
                type="submit" 
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : `Complete Purchase - ${selectedPlan.price} (one-time payment)`}
              </SubmitButton>
            </FormSection>
          </form>
          
          <LoginLink>
            Already have an account? <a href="/login" onClick={(e) => {
              e.preventDefault();
              scrollToTop();
              navigate('/login');
            }}>Log In</a>
          </LoginLink>
        </FormContainer>

        <DisclaimerText>
            <strong>Important:</strong> Dozzy.au provides AI-generated personalized sleep plans based on guidelines from respected Australian organizations. 
            Our service is not medical advice. 
            
          </DisclaimerText>
      </PageContainer>
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingContainer>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Processing your payment and creating your account...</p>
          </LoadingContainer>
        </LoadingOverlay>
      )}
    </div>
  );
};

export default SignupPaymentPage;
